// ============================================================
// FILE: PublicGardenerProfile.js
// LOCATION: C:\gardenmarket\web\src\pages\buyer\PublicGardenerProfile.js
// ============================================================
// PURPOSE:
//   Read-only public profile page a buyer sees when clicking a
//   gardener card from BrowseGardeners.js. Shows summary stats
//   (rating, jobs completed, 5-star count), portfolio photos
//   (NEW), and the full list of reviews left by previous buyers.
//
// ROUTE: /gardener-profile/:id   (id = gardener_id, NOT user_id)
//
// CALLS API:
//   GET /api/reviews/gardener/:id
//       Returns two things in one call:
//         - summary: aggregate stats (avg_rating, total_jobs, etc.)
//         - reviews: paginated list of individual reviews
//   GET /api/photos/portfolio/:id  (via PortfolioGallery.js)
//
// USED BY:
//   BrowseGardeners.js — each gardener card links here
//
// NOTES FOR MAINTAINERS:
//   - This is intentionally read-only and has no edit controls —
//     contrast with GardenerProfile.js which is the gardener's own
//     editable profile page.
//   - Reviewer names are shown as "First L." (see usp_reviews_for_gardener)
//     for privacy — full last names are never exposed publicly.
//   - No pagination controls are wired up yet; the API supports
//     page/page_size params if we need to add "load more" later.
//   - Portfolio section (PortfolioGallery.js) renders nothing if
//     the gardener has no photos — no empty card shown.
// ============================================================

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import client from '../../api/client';
import PortfolioGallery from './PortfolioGallery';

export default function PublicGardenerProfile() {
    const { id } = useParams(); // gardener_id from the URL

    const [summary, setSummary] = useState(null); // aggregate stats
    const [reviews, setReviews] = useState([]);    // individual review list
    const [loading,  setLoading]  = useState(true);

    useEffect(() => {
        client.get(`/reviews/gardener/${id}`)
            .then(r => {
                setSummary(r.data.summary);
                setReviews(r.data.reviews);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div style={styles.loading}>Loading profile...</div>;

    return (
        <div style={styles.page}>
            <div style={styles.backRow}>
                <Link to="/gardeners" style={styles.back}>← Back to Search</Link>
            </div>

            {/* ── Summary stats card ── */}
            <div style={styles.header}>
                <div style={styles.statsRow}>
                    <div style={styles.stat}>
                        <div style={styles.statNum}>{summary?.avg_rating ? Number(summary.avg_rating).toFixed(1) : '—'}</div>
                        <div style={styles.statLabel}>Avg rating</div>
                    </div>
                    <div style={styles.stat}>
                        <div style={styles.statNum}>{summary?.total_jobs || 0}</div>
                        <div style={styles.statLabel}>Jobs completed</div>
                    </div>
                    <div style={styles.stat}>
                        <div style={styles.statNum}>{summary?.five_star_count || 0}</div>
                        <div style={styles.statLabel}>5-star reviews</div>
                    </div>
                    <div style={styles.stat}>
                        <div style={styles.statNum}>{summary?.total_reviews || 0}</div>
                        <div style={styles.statLabel}>Total reviews</div>
                    </div>
                </div>
            </div>

            {/* ── NEW: Portfolio gallery — renders nothing if empty ── */}
            <PortfolioGallery gardenerId={id} />

            {/* ── Individual reviews list ── */}
            <div style={styles.card}>
                <h3 style={styles.cardTitle}>Reviews</h3>
                {reviews.length === 0 && (
                    <p style={styles.noReviews}>No reviews yet.</p>
                )}
                {reviews.map(r => (
                    <div key={r.review_id} style={styles.review}>
                        <div style={styles.reviewHeader}>
                            {/* Filled stars for the rating, empty for the remainder out of 5 */}
                            <span style={styles.stars}>{'★'.repeat(r.star_rating)}{'☆'.repeat(5 - r.star_rating)}</span>
                            <span style={styles.reviewDate}>{new Date(r.created_at).toLocaleDateString()}</span>
                        </div>
                        {r.comment && <p style={styles.comment}>{r.comment}</p>}
                        <p style={styles.reviewMeta}>
                            {r.reviewer_display_name} — {r.job_title} ({r.suburb_name})
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Inline styles (kept local to this component) ──────────
const styles = {
    page:        { maxWidth:'700px', margin:'0 auto', padding:'32px 24px' },
    loading:     { textAlign:'center', padding:'60px', color:'#666' },
    backRow:     { marginBottom:'16px' },
    back:        { color:'#2d6a4f', fontSize:'0.9rem' },
    header:      { background:'white', borderRadius:'10px', padding:'24px', boxShadow:'0 2px 8px rgba(0,0,0,0.07)', marginBottom:'20px' },
    statsRow:    { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'16px' },
    stat:        { textAlign:'center' },
    statNum:     { fontSize:'1.8rem', fontWeight:'bold', color:'#2d6a4f' },
    statLabel:   { fontSize:'0.8rem', color:'#888', marginTop:'4px' },
    card:        { background:'white', borderRadius:'10px', padding:'24px', boxShadow:'0 2px 8px rgba(0,0,0,0.07)' },
    cardTitle:   { color:'#2d6a4f', marginBottom:'16px' },
    noReviews:   { color:'#888', textAlign:'center', padding:'20px' },
    review:      { borderBottom:'1px solid #eee', padding:'14px 0' },
    reviewHeader:{ display:'flex', justifyContent:'space-between', marginBottom:'6px' },
    stars:       { color:'#FFC107', fontSize:'1.1rem' },
    reviewDate:  { color:'#999', fontSize:'0.82rem' },
    comment:     { color:'#444', fontSize:'0.92rem', lineHeight:'1.5', marginBottom:'6px' },
    reviewMeta:  { color:'#888', fontSize:'0.8rem' },
};
